/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { observer } from 'mobx-react';
import { Progress, Icon, Popup } from 'semantic-ui-react';

// Icon style fix for alignment
const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

/**
 * StorageUsageDisplay component displays disk utilization with a progress bar.
 * 
 * Props:
 * - totalGB: Total storage size in GB (required)
 * - usedGB: Used storage in GB (optional, can be null/undefined)
 * - hideWarningIcon: If true, hides the inline warning icon (for when it's shown elsewhere)
 * 
 * Requirements:
 * - Display progress bar showing utilization percentage
 * - Display text: "{usedGB} GB/{totalGB} GB" with 1 decimal precision
 * - Show warning indicator when usedGB > 80% of totalGB (unless hideWarningIcon is true)
 * - Handle null/undefined usedGB gracefully (show only total)
 */
const StorageUsageDisplay = observer(({ totalGB, usedGB, hideWarningIcon = false }) => {
  // Parse totalGB to ensure it's a number
  const total = parseFloat(totalGB);
  
  // Check if totalGB is valid
  if (!_.isFinite(total) || total <= 0) {
    return (
      <div className="storage-usage-display">
        <span className="storage-usage-text">Storage: Unavailable</span>
      </div>
    );
  }

  // Check if usedGB is valid (not null, undefined, NaN, Infinity, or exactly 0.0)
  const used = parseFloat(usedGB);
  const hasUsage = _.isFinite(used) && usedGB !== null && usedGB !== undefined && used > 0;

  // If no usage data, show only total storage
  if (!hasUsage) {
    return (
      <div className="storage-usage-display" style={{ textAlign: 'right' }}>
        <span className="storage-usage-text">{total.toFixed(1)} GB total</span>
      </div>
    );
  }

  // Calculate percentage
  const percentage = Math.min((used / total) * 100, 100);
  const isWarning = percentage > 80;

  // Format values to 1 decimal place
  const usedFormatted = used.toFixed(1);
  const totalFormatted = total.toFixed(1);

  return (
    <div className="storage-usage-display" style={{ textAlign: 'right' }}>
      <div className="storage-usage-header" style={{ justifyContent: 'flex-end' }}>
        <span className="storage-usage-text">
          {usedFormatted} GB/{totalFormatted} GB
        </span>
        {isWarning && !hideWarningIcon && (
          <Popup
            trigger={
              <Icon 
                name="warning sign" 
                style={{ ...iconStyle, color: '#dc2626', marginLeft: '6px' }} 
                data-testid="storage-warning-icon"
              />
            }
            content="Storage usage is above 80%. Consider freeing up space or expanding storage."
            size="small"
            position="top center"
          />
        )}
      </div>
      <Progress
        percent={percentage}
        size="tiny"
        color={isWarning ? 'red' : 'green'}
        className="storage-usage-progress"
        style={{ marginBottom: 0, marginTop: '6px' }}
      />
    </div>
  );
});

StorageUsageDisplay.displayName = 'StorageUsageDisplay';

export default StorageUsageDisplay;
